/**
 * +patrols: out-of-combat NPC movement (docs/design/mob-movement.md), modelled on the TrinityCore
 * movement generators:
 *   - idle (MovementType 0): stands at its spawn; opt-in packs turn a little now and then;
 *   - wander (MovementType 1, RandomMovementGenerator): walks to a random point within `radius` of
 *     its spawn, pauses WANDER_PAUSE_MS, repeats;
 *   - waypoint patrol (MovementType 2, WaypointMovementGenerator) for a pack leader: walks the path
 *     with per-point pauses/facing, looping or ping-ponging;
 *   - formation (CreatureGroups / FormationMovementGenerator): the other members of a patrol pack
 *     follow the leader at a fixed (along, lateral) offset taken from their spawn positions. They
 *     follow the leader's breadcrumb trail, so at corners they trace its path, and they pull the
 *     lateral offset in where the slot would be inside a wall (doorways, narrow corridors).
 * All randomness goes through ctx.rng; all state lives in AiState.roam (serializable).
 */
import type { PackDef, PatrolPath, RoamState, SimContext, SpawnMovement, Unit, Vec2 } from '../contracts';
import { FIDGET_MS, WANDER_BURST, WANDER_PAUSE_MS } from '../contracts';

/** A walker within this distance of a point has arrived. */
const ARRIVE = 0.5;
/** Leader breadcrumb spacing (yd). */
const TRAIL_STEP = 0.75;
/** Breadcrumbs kept beyond the farthest follower (yd). */
const TRAIL_SPARE = 3;
/** A follower closer than this to its slot stands still. */
const SLOT_ARRIVE = 0.35;
/** A follower re-targets its slot once the slot moved this far from its current destination. */
const SLOT_REPATH = 0.4;
/** A follower further than this behind its slot runs to catch up (else it walks). */
const CATCH_UP = 2.5;
/** Fidget turn (radians either side of the spawn facing). */
const FIDGET_TURN = 0.45;
/** Wander: attempts to find a reachable random point before pausing again. */
const WANDER_TRIES = 4;

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const angleTo = (a: Vec2, b: Vec2): number => Math.atan2(b.y - a.y, b.x - a.x);

/** Formation offset of a member relative to its leader: `along` the leader's heading (negative = behind), `lateral` to its left. */
export interface FormationOffset {
  along: number;
  lateral: number;
}

/** Offsets of every spawn of a pack relative to spawns[0] and its spawn facing (TC creature_formations dist/angle). */
export function formationOffsets(def: PackDef): FormationOffset[] {
  const lead = def.spawns[0];
  if (!lead) return [];
  const c = Math.cos(lead.facing);
  const s = Math.sin(lead.facing);
  return def.spawns.map((sp) => {
    const rx = sp.pos.x - lead.pos.x;
    const ry = sp.pos.y - lead.pos.y;
    return { along: rx * c + ry * s, lateral: -rx * s + ry * c };
  });
}

/** Geometry the formation needs (the spatial system in the sim; plain functions in tests). */
export interface FormationGeo {
  /** Straight walkable line between two points. */
  clear(a: Vec2, b: Vec2): boolean;
}

/**
 * Where a follower with `off` should stand. `trail` is the leader's breadcrumbs, newest first
 * (the leader's current position is `leaderPos`). Members behind the leader sit on its trail
 * `-along` yards back (so they turn corners where it turned); the lateral offset is applied
 * across the trail direction and shrunk (to half, then zero) where it would cross a wall.
 * Returns null when the trail is too short yet (the follower stays where it is).
 */
export function formationSlot(leaderPos: Vec2, leaderFacing: number, trail: readonly Vec2[], off: FormationOffset, geo: FormationGeo): Vec2 | null {
  let base: Vec2;
  let dir: number;
  if (off.along >= 0) {
    dir = leaderFacing;
    base = { x: leaderPos.x + Math.cos(dir) * off.along, y: leaderPos.y + Math.sin(dir) * off.along };
    if (!geo.clear(leaderPos, base)) base = leaderPos;
  } else {
    let need = -off.along;
    let prev = leaderPos;
    let found: { p: Vec2; dir: number } | null = null;
    for (const pt of trail) {
      const seg = dist(prev, pt);
      if (seg <= 1e-6) continue;
      if (seg >= need) {
        const t = need / seg;
        found = { p: { x: prev.x + (pt.x - prev.x) * t, y: prev.y + (pt.y - prev.y) * t }, dir: angleTo(pt, prev) };
        break;
      }
      need -= seg;
      prev = pt;
    }
    if (!found) return null;
    base = found.p;
    dir = found.dir;
  }
  if (off.lateral === 0) return base;
  const nx = -Math.sin(dir);
  const ny = Math.cos(dir);
  for (const k of [1, 0.5]) {
    const p = { x: base.x + nx * off.lateral * k, y: base.y + ny * off.lateral * k };
    if (geo.clear(base, p)) return p;
  }
  return base;
}

/** Next waypoint index and direction after arriving at `i` (loop or ping-pong). */
export function nextPatrolIndex(path: PatrolPath, i: number, dir: 1 | -1): { index: number; dir: 1 | -1 } {
  const n = path.points.length;
  if (n <= 1) return { index: 0, dir };
  if (path.mode === 'loop') return { index: (i + 1) % n, dir: 1 };
  let d = dir;
  if (i + d < 0 || i + d >= n) d = -d as 1 | -1;
  return { index: i + d, dir: d };
}

/** Lazily created roam state; the first idle decision is staggered so packs don't fidget in sync. */
function roamState(ctx: SimContext, npc: Unit): RoamState {
  const ai = npc.ai!;
  ai.roam ??= { nextAt: ctx.now + ctx.rng.range(0, FIDGET_MS[1]), dir: 1 };
  return ai.roam;
}

function stop(npc: Unit): void {
  if (npc.move && npc.move.mode !== 'forced') npc.move = null;
}

/** Walk (or run) toward `p`; re-paths only when the destination moved. False if unreachable. */
function walkTo(ctx: SimContext, npc: Unit, p: Vec2, walk: boolean): boolean {
  const m = npc.move;
  if (m?.mode === 'dest' && dist(m.dest, p) <= SLOT_REPATH && !!m.walk === walk) return true;
  if (!ctx.sys.spatial.setDestination(ctx, npc.id, p)) return false;
  if (npc.move?.mode === 'dest' && walk) npc.move.walk = true;
  return true;
}

const clearLine = (ctx: SimContext) => (a: Vec2, b: Vec2): boolean => {
  const sp = ctx.sys.spatial;
  if (!sp.isWalkable(ctx.world, b)) return false;
  return sp.lineOfSight ? sp.lineOfSight(ctx.world, a, b) : true;
};

/** Spawn index of a pack member (pack member ids are created in spawn order). */
function spawnIndex(ctx: SimContext, npc: Unit): number {
  return ctx.world.packs[npc.packId ?? '']?.memberIds.indexOf(npc.id) ?? -1;
}

/** The pack's current patrol leader: the first living member in spawn order. */
export function patrolLeader(ctx: SimContext, packId: string): Unit | undefined {
  for (const id of ctx.world.packs[packId]?.memberIds ?? []) {
    const u = ctx.world.units[id];
    if (u?.alive && u.ai) return u;
  }
  return undefined;
}

/**
 * One out-of-combat update for an opt-in or moving NPC. Returns true if this NPC roams at all
 * (so the caller can leave legacy NPCs alone).
 */
export function roam(ctx: SimContext, npc: Unit, def: PackDef | undefined): boolean {
  const ai = npc.ai!;
  const path = def?.path;
  if (def && path && path.points.length > 0) {
    ai.mode = 'patrol';
    const leader = patrolLeader(ctx, def.id);
    if (leader === npc) leadPatrol(ctx, npc, path);
    else if (leader) follow(ctx, npc, leader, def);
    return true;
  }
  const idx = def ? spawnIndex(ctx, npc) : -1;
  const movement: SpawnMovement | undefined = idx >= 0 ? def!.spawns[idx]?.movement : undefined;
  if (movement?.type === 'wander' && movement.radius > 0) {
    ai.mode = 'patrol';
    wander(ctx, npc, movement.radius, movement.pauseMs ?? WANDER_PAUSE_MS, npc.home);
    return true;
  }
  if (def?.link || movement?.type === 'idle') {
    fidget(ctx, npc);
    return true;
  }
  return false;
}

/**
 * +field: out-of-combat movement of a field mob: wander around its spawn point `center` (not its
 * own scattered home, so a spawn's mobs share one area), or stand and fidget.
 */
export function roamSpawn(ctx: SimContext, npc: Unit, movement: SpawnMovement, center: Vec2): void {
  if (movement.type === 'wander' && movement.radius > 0) {
    npc.ai!.mode = 'patrol';
    wander(ctx, npc, movement.radius, movement.pauseMs ?? WANDER_PAUSE_MS, center);
  } else if (movement.type === 'patrol' && movement.points.length > 0) {
    npc.ai!.mode = 'patrol';
    walkRoute(ctx, npc, movement.points, movement.mode, movement.pauseMs ?? [0, 0]);
  } else fidget(ctx, npc);
}

/**
 * +zone: a field patrol: each mob walks the route on its own (starting at the nearest point),
 * pausing at every point. After an evade it resumes from where it was pulled.
 */
function walkRoute(ctx: SimContext, npc: Unit, points: readonly Vec2[], mode: 'loop' | 'pingpong', pause: readonly [number, number]): void {
  const ai = npc.ai!;
  const st = roamState(ctx, npc);
  if (st.going === undefined) {
    // First step: head for the nearest point of the route.
    let best = 0;
    for (let i = 1; i < points.length; i++) if (dist(points[i]!, npc.pos) < dist(points[best]!, npc.pos)) best = i;
    ai.patrolIndex = best;
    st.going = false;
    st.nextAt = ctx.now;
  }
  if (ctx.now < st.nextAt) return;
  if (ai.patrolIndex >= points.length) ai.patrolIndex = 0;
  const pt = points[ai.patrolIndex]!;
  if (dist(npc.pos, pt) <= ARRIVE + 0.5) {
    stop(npc);
    const nx = nextPatrolIndex({ points: points.map((p) => ({ pos: p })), mode }, ai.patrolIndex, st.dir);
    ai.patrolIndex = nx.index;
    st.dir = nx.dir;
    st.nextAt = ctx.now + (pause[1] > pause[0] ? ctx.rng.range(pause[0], pause[1]) : pause[0]);
    return;
  }
  if (!walkTo(ctx, npc, pt, true)) {
    const nx = nextPatrolIndex({ points: points.map((p) => ({ pos: p })), mode }, ai.patrolIndex, st.dir);
    ai.patrolIndex = nx.index;
    st.dir = nx.dir;
    st.nextAt = ctx.now + 1000;
  }
}

function fidget(ctx: SimContext, npc: Unit): void {
  const st = roamState(ctx, npc);
  if (ctx.now < st.nextAt || npc.move) return;
  npc.facing = npc.homeFacing + ctx.rng.range(-FIDGET_TURN, FIDGET_TURN);
  st.nextAt = ctx.now + ctx.rng.range(FIDGET_MS[0], FIDGET_MS[1]);
}

function wander(ctx: SimContext, npc: Unit, radius: number, pause: readonly [number, number], center: Vec2): void {
  const st = roamState(ctx, npc);
  if (st.going) {
    if (npc.move) return;
    // Arrived (or blocked): next move of the burst right away, else pause.
    st.going = false;
    st.steps = (st.steps ?? 1) - 1;
    if (st.steps > 0) st.nextAt = ctx.now;
    else st.nextAt = ctx.now + ctx.rng.range(pause[0], pause[1]);
    return;
  }
  if (ctx.now < st.nextAt) return;
  if (!st.steps || st.steps <= 0) st.steps = ctx.rng.int(WANDER_BURST[0], WANDER_BURST[1]);
  const ok = clearLine(ctx);
  for (let i = 0; i < WANDER_TRIES; i++) {
    // TC 3.3.5: uniform angle and uniform distance (0..radius) from the spawn, in line of sight.
    const a = ctx.rng.range(0, Math.PI * 2);
    const r = radius * ctx.rng.next();
    const p = { x: center.x + Math.cos(a) * r, y: center.y + Math.sin(a) * r };
    if (dist(p, npc.pos) < 1 || !ok(npc.pos, p)) continue;
    if (walkTo(ctx, npc, p, true)) {
      st.going = true;
      return;
    }
  }
  st.steps = 0;
  st.nextAt = ctx.now + ctx.rng.range(pause[0], pause[1]);
}

function leadPatrol(ctx: SimContext, npc: Unit, path: PatrolPath): void {
  const ai = npc.ai!;
  const st = roamState(ctx, npc);
  if (!st.trail) st.nextAt = ctx.now; // patrols start walking at once
  st.trail ??= [];
  recordTrail(ctx, npc, st);
  if (ctx.now < st.nextAt) return; // pausing at a point
  if (ai.patrolIndex >= path.points.length) ai.patrolIndex = 0;
  const pt = path.points[ai.patrolIndex]!;
  if (dist(npc.pos, pt.pos) <= ARRIVE) {
    stop(npc);
    if (pt.facing !== undefined) npc.facing = pt.facing;
    const nx = nextPatrolIndex(path, ai.patrolIndex, st.dir);
    ai.patrolIndex = nx.index;
    st.dir = nx.dir;
    if ((pt.waitMs ?? 0) > 0) {
      st.nextAt = ctx.now + pt.waitMs!;
      return;
    }
  }
  const target = path.points[ai.patrolIndex]!.pos;
  if (!walkTo(ctx, npc, target, !path.run)) {
    // Unreachable point (a closed door): skip it.
    const nx = nextPatrolIndex(path, ai.patrolIndex, st.dir);
    ai.patrolIndex = nx.index;
    st.dir = nx.dir;
  }
}

/** Leader breadcrumbs, newest first, trimmed to what the farthest follower needs. */
function recordTrail(ctx: SimContext, npc: Unit, st: RoamState): void {
  const trail = st.trail!;
  const def = ctx.content.dungeon(ctx.world.dungeonId)?.packs.find((p) => p.id === npc.packId);
  const keep = maxFollowDistance(def) + TRAIL_SPARE;
  if (trail.length === 0) {
    // Seed a straight trail behind the leader so followers have slots before it moves.
    const back = npc.homeFacing + Math.PI;
    trail.push({ x: npc.pos.x, y: npc.pos.y }, { x: npc.pos.x + Math.cos(back) * keep, y: npc.pos.y + Math.sin(back) * keep });
    return;
  }
  if (dist(npc.pos, trail[0]!) >= TRAIL_STEP) trail.unshift({ x: npc.pos.x, y: npc.pos.y });
  let len = dist(npc.pos, trail[0]!);
  for (let i = 1; i < trail.length; i++) {
    len += dist(trail[i - 1]!, trail[i]!);
    if (len > keep) {
      trail.length = i + 1;
      break;
    }
  }
}

const followCache = new WeakMap<PackDef, { offsets: FormationOffset[]; max: number }>();
function formation(def: PackDef): { offsets: FormationOffset[]; max: number } {
  let f = followCache.get(def);
  if (!f) {
    const offsets = formationOffsets(def);
    f = { offsets, max: Math.max(0, ...offsets.map((o) => Math.hypot(o.along, o.lateral))) };
    followCache.set(def, f);
  }
  return f;
}
const maxFollowDistance = (def: PackDef | undefined): number => (def ? formation(def).max : 0);

function follow(ctx: SimContext, npc: Unit, leader: Unit, def: PackDef): void {
  const { offsets } = formation(def);
  const mine = offsets[spawnIndex(ctx, npc)];
  const lead = offsets[spawnIndex(ctx, leader)];
  if (!mine || !lead) return;
  const off = { along: mine.along - lead.along, lateral: mine.lateral - lead.lateral };
  const trail = leader.ai?.roam?.trail ?? [];
  const slot = formationSlot(leader.pos, leader.facing, trail, off, { clear: clearLine(ctx) });
  if (!slot) return;
  const d = dist(npc.pos, slot);
  if (d <= SLOT_ARRIVE) {
    stop(npc);
    if (!leader.moving) npc.facing = leader.facing;
    return;
  }
  walkTo(ctx, npc, slot, d <= CATCH_UP && !def.path?.run);
}
